/**
 * Utility class for numeral and string formatting operations
 */
export class Numerals {
  // Unicode superscript characters for digits 0-9 and symbols - +
  public static readonly SUPER_SCRIPT_DATA = '⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺';
  // Unicode subscript characters for digits 0-9 and symbols - +
  public static readonly SUB_SCRIPT_DATA = '₀₁₂₃₄₅₆₇₈₉₋₊';

  /**
   * Converts a number to superscript or subscript representation
   * @param data - The character set (SUPER_SCRIPT_DATA or SUB_SCRIPT_DATA)
   * @param value - The integer value to convert
   * @returns The formatted string in superscript/subscript
   */
  private static script(data: string, value: number): string {
    let result = '';
    let num = value;

    // Handle negative numbers
    if (num < 0) {
      // Get the minus sign character (index 10 or 11 depending on data)
      const minusIndex = data === this.SUPER_SCRIPT_DATA ? 10 : 11;
      result += data[minusIndex];
      num = -num;
    }

    // Convert each digit to the corresponding script character
    const numStr = num.toString();
    for (let i = 0; i < numStr.length; i++) {
      const char = numStr[i];
      if (char >= '0' && char <= '9') {
        const index = char.charCodeAt(0) - '0'.charCodeAt(0);
        result += data[index];
      }
    }

    return result;
  }

  /**
   * Converts a number to superscript representation
   * @param value - The integer value
   * @returns The value in superscript (e.g., 123 → ¹²³)
   */
  public static superScript(value: number): string {
    return this.script(this.SUPER_SCRIPT_DATA, value);
  }

  /**
   * Converts a number to subscript representation
   * @param value - The integer value
   * @returns The value in subscript (e.g., 123 → ₁₂₃)
   */
  public static subScript(value: number): string {
    return this.script(this.SUB_SCRIPT_DATA, value);
  }

  /**
   * Converts a number to English words
   * @param number - The integer value
   * @returns The number in English words
   */
  public static toEnglishString(number: number): string {
    if (number === 0) return 'zero';
    if (number < 0) return `minus ${  this.toEnglishString(Math.abs(number))}`;

    const words: string[] = [];
    const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = [
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      'ten',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];

    let n = number;

    // Handle millions
    if (Math.floor(n / 1000000) > 0) {
      words.push(`${this.toEnglishString(Math.floor(n / 1000000))  } million`);
      n %= 1000000;
    }

    // Handle thousands
    if (Math.floor(n / 1000) > 0) {
      words.push(`${this.toEnglishString(Math.floor(n / 1000))  } thousand`);
      n %= 1000;
    }

    // Handle hundreds
    if (Math.floor(n / 100) > 0) {
      words.push(`${this.toEnglishString(Math.floor(n / 100))  } hundred`);
      n %= 100;
    }

    // Handle tens and ones
    if (n > 0) {
      if (words.length > 0) {
        words.push('and');
      }

      if (n < 10) {
        words.push(units[n]);
      } else if (n < 20) {
        words.push(teens[n - 10]);
      } else {
        const tensWord = tens[Math.floor(n / 10)];
        const unitsWord = n % 10 > 0 ? `-${  units[n % 10]}` : '';
        words.push(tensWord + unitsWord);
      }
    }

    return words.join(' ').replace(/\s+/g, ' ').trim();
  }

  /**
   * Converts a number to Roman numerals
   * @param number - The integer value (1-3999)
   * @returns The number in Roman numerals
   */
  public static toRomanString(number: number): string {
    if (number < 1 || number > 3999) {
      throw new Error('Roman numerals can only represent numbers from 1 to 3999');
    }

    const romanMap: [number, string][] = [
      [1000, 'M'],
      [900, 'CM'],
      [500, 'D'],
      [400, 'CD'],
      [100, 'C'],
      [90, 'XC'],
      [50, 'L'],
      [40, 'XL'],
      [10, 'X'],
      [9, 'IX'],
      [5, 'V'],
      [4, 'IV'],
      [1, 'I'],
    ];

    let result = '';
    let n = number;

    for (const [value, symbol] of romanMap) {
      while (n >= value) {
        result += symbol;
        n -= value;
      }
    }

    return result;
  }

  /**
   * Formats a number with a plus sign for positive values
   * Uses proper minus sign (U+2212) for negative values
   * @param n - The number to format
   * @returns Formatted string (e.g., "+ 5" or "− 3")
   */
  public static strAdd(n: number): string {
    return n >= 0 ? `+ ${n}` : `− ${Math.abs(n)}`;
  }

  /**
   * Formats a number with proper minus sign (U+2212) for negative values
   * @param n - The number to format
   * @returns Formatted string (e.g., "5" or "−3")
   */
  public static strNum(n: number): string {
    return n >= 0 ? `${n}` : `−${Math.abs(n)}`;
  }

  /**
   * Special formatting for coefficient 1 or -1
   * @param n - The number to format
   * @returns Empty string for 1, "−" for -1, otherwise strNum(n)
   */
  public static strOne(n: number): string {
    if (n === 1) return '';
    if (n === -1) return '−';
    return this.strNum(n);
  }

  /**
   * Formats a coefficient for polynomial terms
   * @param n - The coefficient
   * @param first - Whether this is the first term in the polynomial
   * @returns Formatted coefficient string
   */
  public static strAddOne(n: number, first: boolean): string {
    if (n === 1 && first) return '';
    if (n === 1) return ' + ';
    if (n === -1) return ' − ';
    if (first) return this.strNum(n);
    return this.strAdd(n);
  }

  /**
   * Counts the number of digits in an integer
   * @param number - The integer value (number or bigint)
   * @returns Number of digits (including minus sign for negative numbers)
   */
  public static digitsCount(number: number | bigint): number {
    if (typeof number === 'number') {
      if (number === 0) return 1;
      return Math.floor(Math.log10(Math.abs(number))) + 1;
    } else {
      // For bigint
      if (number === 0n) return 1;
      let count = 0;
      let absNumber = number < 0n ? -number : number;

      while (absNumber > 0n) {
        absNumber /= 10n;
        count++;
      }

      return count;
    }
  }

  /**
   * Alternative implementation using string conversion for digit counting
   */
  public static digitsCountString(number: number | bigint): number {
    const str = number.toString();
    // Count only digits (ignore minus sign)
    const digitCount = str.replace(/[^0-9]/g, '').length;
    return digitCount || 1; // Return 1 for 0
  }
}
